import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import dayjs from 'dayjs';

import {
  getDefaultRevenueChartFilterValues,
  getRevenueChartComparisonMode,
  type RevenueChartFilter,
} from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/RevenueChart/components/RevenueChartFilter';
import {
  getRevenueAnalyticsGroupBy,
  mapRevenueAnalyticsToChartData,
} from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/hooks/map-revenue-analytics-response';
import RevenueChart from '@/graphql/dashboard/RevenueChart';
import { useCommonApolloOptions } from '@/hooks/use-common-apollo-options';

import { DashboardV2GroupBy } from '../../../../../../../../../../generated/graphql';

export type {
  RevenueChartCategory,
  RevenueChartCategoryData,
  RevenueChartData,
} from './map-revenue-analytics-response';
export type { RevenueChartGroupBy } from '@/utils/date';

type UseRevenueChartOptions = {
  filter?: RevenueChartFilter;
  gateGroupId?: string;
};

export function useRevenueChartData(options: UseRevenueChartOptions) {
  const { filter, gateGroupId } = options;

  const apolloOptions = useCommonApolloOptions();
  const resolvedFilter = filter ?? getDefaultRevenueChartFilterValues();
  const groupByKey = getRevenueAnalyticsGroupBy(resolvedFilter.dateFrom, resolvedFilter.dateTo);

  const query = useQuery(RevenueChart, {
    ...apolloOptions,
    variables: {
      date_from: dayjs(resolvedFilter.dateFrom).format('YYYY-MM-DD'),
      date_to: dayjs(resolvedFilter.dateTo).format('YYYY-MM-DD'),
      comparison_mode: getRevenueChartComparisonMode(resolvedFilter),
      group_by: groupByKey === 'hour' ? DashboardV2GroupBy.Hour : DashboardV2GroupBy.Day,
      currency_id: resolvedFilter.currency.id,
      operation_type: resolvedFilter.operationType,
      gate_group_id: gateGroupId,
    },
    skip: !filter,
  });

  const chartsData = useMemo(() => {
    if (!query.data?.dashboardWidgetsRevenueAnalyticsV2) {
      return undefined;
    }

    return mapRevenueAnalyticsToChartData(query.data.dashboardWidgetsRevenueAnalyticsV2);
  }, [query.data]);

  return {
    ...query,
    chartsData,
    groupBy: groupByKey,
  };
}
